<script setup lang="ts">
import {onKeyStroke, useRefHistory, watchDebounced} from "@vueuse/core";
import {computed, nextTick, ref, watch} from 'vue';
import {Icon} from '@iconify/vue';

// Composables
import {useOsmdInteractiveScore} from "@/composables/useOsmdInteractiveScore";
import type {ScoreNotificationType} from "@/composables/osmd/useScoreEditActions";
import {usePdfExport} from "@/composables/usePdfExport";
import {useNotificationStore} from "@/stores/notifications";

// Sub-components – Selektions-/Edit-/Player-State erhalten sie über inject (ScoreEditContext),
// den das Composable bereitstellt; daher hier kein Prop-Drilling mehr.
import ZoomControls from './musicxml-preview/ZoomControls.vue';
import ScoreEditToolbar from './musicxml-preview/ScoreEditToolbar.vue';
import PlaybackControlsOverlay from './musicxml-preview/PlaybackControlsOverlay.vue';
import MusicXmlSourceEditor from './musicxml-preview/MusicXmlSourceEditor.vue';
import KeyboardShortcutsDialog from './musicxml-preview/KeyboardShortcutsDialog.vue';
import DeleteChoiceDialog from './musicxml-preview/DeleteChoiceDialog.vue';
import BassLyricPopover from './musicxml-preview/BassLyricPopover.vue';
import LyricEntryOverlay from './musicxml-preview/LyricEntryOverlay.vue';
import {provideToolbarExtensions} from './musicxml-preview/toolbarExtensionsContext';
import VoiceMergePanel from "@/components/transposition/VoiceMergePanel.vue";
import ChordInferencePanel from "@/components/transposition/ChordInferencePanel.vue";
import NoteActionsOverlay from "@/components/musicxml-preview/NoteActionsOverlay.vue";
import {actionsMenuHasContent} from '@/components/musicxml-preview/noteActionsMenu';
import NoteHoverTooltip from "@/components/musicxml-preview/NoteHoverTooltip.vue";
import {hoverTooltipSuppressed} from './musicxml-preview/noteHoverTooltip';
import NoteGripDialog from "@/components/harmonika/NoteGripDialog.vue";
import {ChordInferenceBadge} from "@/components/chord-inference";
import {chordInferenceStatus, readBassLyricText, type ChordInferenceStatus, type NoteInfo} from "@/composables/musicXmlDom";
import type {Grip} from "@/components/harmonika/gripTypes";

// ============ Props & Emits ============

// enableGrips schaltet die Griff-Werkzeuge (Toolbar-Dialog + Rechtsklick-Popover) frei – nur auf
// der Griffschrift-AUSGABE sinnvoll (die Note kodiert ihren Griff selbst). Default aus, damit die
// Eingabe-Vorschau ihr natives Kontextmenü behält.
// enableBassEdit schaltet die Bass-Notations-Bearbeitung (Toolbar-Button + Popover + Rechtsklick-
// Launcher) frei – ebenfalls nur auf der Griffschrift-AUSGABE (nur dort gibt es die Bass-Zeile).
// showChordInferenceStatus blendet den Status-Chip der automatischen Akkorderkennung in der
// Toolbar ein. Default aus und bewusst opt-in (analog enableGrips): nur die EINGABE-Vorschau soll
// ihn zeigen – auf der Griffschrift-Ausgabe wäre er bedeutungslos.
const {
  file = undefined,
  enableGrips = false,
  enableBassEdit = false,
  enableLyricEdit = true,
  enablePlayback = false,
  showAdvancedTools = true,
  showChordInferenceStatus = false,
  showInputTransformPanels = false,
  enableRebar = false,
  unplayableNotes = [],
} = defineProps<{
  file?: string;
  enableGrips?: boolean;
  enableBassEdit?: boolean;
  /** Gesangstext-Eingabe freischalten. Anders als Griff/Bass auf BEIDEN Vorschauen sinnvoll –
   *  `PreviewPane` schaltet sie für Eingabe und Ausgabe getrennt frei (unterschiedliche
   *  Schreibpfade, siehe dort). */
  enableLyricEdit?: boolean;
  /** Wiedergabe (Audio-Player + Leertaste) freischalten. Default aus und bewusst opt-in: die
   *  Griffschrift-AUSGABE ist keine spielbare Partitur und klänge falsch – `PreviewPane` setzt
   *  das Flag daher nur an der EINGABE-Vorschau. Ohne das Flag entsteht kein `PlaybackManager`,
   *  es werden keine SoundFonts geladen und der `ScorePlaybackContext` wird nicht bereitgestellt
   *  (→ `OsmdPlaybackControls` erscheint gar nicht). */
  enablePlayback?: boolean;
  /** Toolbar-Gruppen Transponieren/Akkord-Tonart-Takt/Diatonisch anzeigen. Default an; die
   *  Griffschrift-AUSGABE setzt `false`, damit die Leiste dort aufgeräumt bleibt. Reine
   *  **Anzeige**-Entrümpelung – die zugehörigen Kurzbefehle bleiben dort aktiv (anders als
   *  `enablePlayback`, das die Wiedergabe wirklich abschaltet). */
  showAdvancedTools?: boolean;
  showChordInferenceStatus?: boolean;
  /** Compact-Varianten von VoiceMergePanel/ChordInferencePanel unter der Vorschau einblenden.
   *  Default aus und bewusst opt-in (analog `showChordInferenceStatus`): beide Panels sind
   *  store-gebunden und transformieren die EINGABE (`selectedFileContent`) – auf der
   *  Griffschrift-Ausgabe wirkten sie sonst still auf den falschen Inhalt. */
  showInputTransformPanels?: boolean;
  /** „Taktart ändern + Noten neu aufteilen" im Taktart-Editor anbieten (Emit `rebar`). Nur an der
   *  EINGABE-Vorschau setzen: das Ergebnis wird die neue Arbeitsbasis; auf der Griffschrift-Ausgabe
   *  sind die Takt-Editoren ohnehin aus. */
  enableRebar?: boolean;
  /** Tonhöhen in music21-Schreibweise (`"B-3"`, `"F#4"`), die auf der Harmonika nicht existieren –
   *  aus `AnalysisResult.unmappable_notes`. Passende Noten werden rot markiert. Nur an der
   *  EINGABE-Vorschau setzen: in der Griffschrift-Ausgabe stehen unspielbare Töne bereits als rote
   *  Pausen, und die dort notierte Tonhöhe ist die Staff-Position des Knopfes, nicht der Klang. */
  unplayableNotes?: string[];
}>();

const emit = defineEmits<{
  (e: 'saveXml', data: { musicXml: string }): void;
  (e: 'applyGrip', payload: { note: NoteInfo; grip: Grip }): void;
  (e: 'applyBassLyric', payload: { notes: NoteInfo[]; text: string | null }): void;
  /** Ergebnis einer Gesangstext-Sitzung – EINMAL am Sitzungsende, nicht je Silbe. */
  (e: 'applyLyrics', payload: { musicXml: string }): void;
  /** Spielweise eines Takts im Bass-System geändert; der Aufrufer baut es neu auf. */
  (e: 'setBassStyle', payload: { measureIndex: number; style: string }): void;
  /** Taktart ab Takt-Index ändern + Noten neu aufteilen; der Aufrufer ruft den Endpunkt. */
  (e: 'rebar', payload: { measureIndex: number; beats: number; beatType: number }): void;
}>();

// ============ State ============

const musicXml = ref<string | null>(file ?? null);
const {undo, redo, canUndo, canRedo, pause: pauseHistory, resume: resumeHistory, commit: commitHistory}
    = useRefHistory(musicXml, {capacity: 50});
const hasUnsavedChanges = ref(false);
// true während programmatischem Laden (Datei/Transpose) – verhindert, dass das initiale
// Befüllen von musicXml fälschlich als ungespeicherte Änderung gewertet wird.
let applyingExternalContent = false;
const zoom = ref(1.25);
const containerRef = ref<HTMLElement | null>(null);
const scrollContainerRef = ref<HTMLElement | null>(null);

// Status der automatischen Akkorderkennung. Bewusst aus der Prop `file` (= gespeicherter Stand)
// abgeleitet, NICHT aus dem lokalen `musicXml`-Ref: letzteres ändert sich im Quelltext-Editor bei
// jedem Tastendruck, was ein vollständiges DOMParser-Reparse je Anschlag auslösen würde. Der Chip
// beschreibt ohnehin die geladene Datei, nicht den ungespeicherten Entwurf.
// oldValue-Pattern, weil der Computed ein Objekt liefert (siehe webapp/src/VUE_RULES.md).
const chordInference = computed<ChordInferenceStatus>((oldValue) => {
  const next = chordInferenceStatus(file ?? '');
  if (oldValue?.state === next.state && oldValue.count === next.count) return oldValue;
  return next;
});

// OSMD-Rendering + Interaktion + Audio-Player + Selektions-/Edit-State leben gekapselt im
// Composable. Es stellt ZWEI Kontexte bereit: `provideScoreEditContext()` (Selektion/Edit/
// Gesangstext – `ScoreEditToolbar`, die Edit-Views, die Overlays) und – nur bei
// `enablePlayback` – `provideScorePlayback()` (`OsmdPlaybackControls`). Beide beziehen ihren
// State per inject, kein Prop-Drilling.
// Der RÜCKGABEWERT des Composables ist bewusst etwas anderes als der bereitgestellte Kontext:
// hier steht nur, was der Host selbst braucht – Template, Undo/Redo und der Space-Handler.
// Die DOM-Handler und `error` gibt es deshalb NUR hier, nicht per inject.
// Vor der Composable-Nutzung deklariert: die Gesangstext-Sitzung meldet Fehler (unauflösbare
// Notenadresse) über `notify` nach außen.
const notificationStore = useNotificationStore();

const {
  playback,
  error: renderError,
  getOsmd,
  handleContainerClick, handleContainerHover, handleContainerMouseLeave,
  handleContainerContextMenu,
  handleContainerPointerDown, handleContainerPointerMove, handleContainerPointerUp,
  cancelDragSelection,
  dragRect, isDragSelecting, consumeDragClick,
  clearSelection,
  selectedMeasure,
  selectedNote,
  selectedNotes,
  isLyricActive,
  startLyricEntry,
  deleteChoicePending,
  hoveredNote,
  hoverX,
  hoverY,
  activeEditTab,
  actionScope,
  selectedNoteX,
  selectedNoteY,
} = useOsmdInteractiveScore(musicXml, containerRef, scrollContainerRef, {
  zoom,
  enablePlayback,
  // Getter statt Wert: die Prop ändert sich mit jeder neuen Analyse, und ein direkt übergebener
  // Wert würde nur einmal beim Setup gelesen. Gleiches Muster wie `OsmdMeasure.vue`
  // (`useOsmdRenderer(() => musicXml, …)`) – das Composable löst ihn per `toValue` auf.
  unplayableNotes: () => unplayableNotes,
  onLyricsCommitted: (updated: string) => emit('applyLyrics', {musicXml: updated}),
  onBassStyleChange: (measureIndex: number, style: string) =>
      emit('setBassStyle', {measureIndex, style}),
  onRebar: enableRebar
      ? (measureIndex: number, beats: number, beatType: number) =>
          emit('rebar', {measureIndex, beats, beatType})
      : undefined,
  // Gesperrt bei ungespeicherten Edits: das Server-Ergebnis ersetzt den `file`-Prop, dessen
  // Watcher `musicXml` überschreibt – die schwebenden Edits gingen still verloren (wie Gesangstext).
  rebarBlockedReason: () => hasUnsavedChanges.value ? 'Bitte zuerst Übernehmen oder Verwerfen' : '',
  notify: (message: string, type: ScoreNotificationType = 'error') =>
      notificationStore.notify(message, type, type === 'error' ? 5000 : 2000),
  // Nachrückendes Löschen nur auf der Eingabe – an denselben Schalter gebunden wie Q/W.
  enableShiftDelete: showAdvancedTools,
});

onKeyStroke(' ', async (e) => {
  // Ohne Wiedergabe (Griffschrift-Ausgabe) ist die Leertaste kein Play/Pause – hier VOR
  // preventDefault aussteigen, damit das Event normal weiterläuft.
  if (!enablePlayback || showSourceEditor.value)
    return;
  e.preventDefault();
  e.stopImmediatePropagation()
  if (playback.isPlaying.value)
    await playback.pause()
  // Während des SoundFont-Ladens ignorieren (wie der gesperrte Play-Button).
  else if (!playback.isLoadingSounds.value) await playback.play()
})

// Strg+Z: Rückganging
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z', (e) => {
  if (!canUndo.value) return;
  e.preventDefault();
  handleUndo();
});
// Strg+Y: Redo
onKeyStroke((e) => (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'y', (e) => {
  if (!canRedo.value) return;
  e.preventDefault();
  handleRedo();
});
// „?" (Umschalt+ß bzw. Umschalt+/): Tastenkürzel-Übersicht ein-/ausblenden. Guard überspringt
// fokussierte Textfelder/Quelltext-Editor, damit „?" dort normal getippt werden kann.
onKeyStroke((e) => e.key === '?', (e) => {
  if (isEditingText()) return;
  e.preventDefault();
  showShortcuts.value = !showShortcuts.value;
});

function isEditingText(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT'
      || el.tagName === 'TEXTAREA'
      || el.isContentEditable
      || !!el.closest('.cm-editor');
}

// UI state
const showErrorDetails = ref(false);
// Der Quelltext-Editor ist eine SPLIT-ANSICHT (kein schwebendes Overlay) und bleibt deshalb ein
// eigener Zustand neben `activeOverlay` – ihn exklusiv zu schalten würde die Ansicht schließen,
// sobald man im Notenbild ein Menü öffnet.
const showSourceEditor = ref(false);

// ============ Schwebende Overlays (genau EINES offen) ============

// EIN Zustand statt vier sich manuell gegenseitig schließender Flags: die Exklusivität ist
// strukturell (der Enum kann nur einen Wert tragen), die früheren Schließ-Kaskaden in jeder
// open*-Funktion entfallen. Die Gesangstext-Sitzung (`isLyricActive`) gehört NICHT dazu – sie
// lebt im Lyric-Composable; `openLyricEntry` schließt die Overlays über `closeOverlays()`.
type PreviewOverlay = 'none' | 'actions' | 'gripDialog' | 'bass' | 'shortcuts';
const activeOverlay = ref<PreviewOverlay>('none');

/** v-model-fähige Sicht auf EIN Overlay: `true` schreiben öffnet es (und schließt damit
 *  strukturell jedes andere), `false` schließt es, ohne ein inzwischen geöffnetes anderes
 *  Overlay zu berühren. */
function overlayModel(name: Exclude<PreviewOverlay, 'none'>) {
  return computed({
    get: () => activeOverlay.value === name,
    set: (open: boolean) => {
      if (open) activeOverlay.value = name;
      else if (activeOverlay.value === name) activeOverlay.value = 'none';
    },
  });
}

function closeOverlays(): void {
  activeOverlay.value = 'none';
}

const showShortcuts = overlayModel('shortcuts');
const showGripPopover = overlayModel('actions');
const showGripDialog = overlayModel('gripDialog');
const showBassPopover = overlayModel('bass');

// ============ Griff (nur bei enableGrips – Griffschrift-Ausgabe) ============

const gripAnchor = ref({x: 0, y: 0});
// Der Toolbar-Button ist nur bei ausgewählter, klingender Note aktiv (Pause hat keinen Griff).
const canEditGrip = computed(() => !!selectedNote.value && !selectedNote.value.isRest);

// Rechtsklick → NoteActionsOverlay am Cursor, auf BEIDEN Vorschauen (Notenschrift UND
// Griffschrift). Welche Einträge es zeigt, entscheidet der Geltungsbereich des Treffers
// (`hit.scope`): eine getroffene Note bringt Info-Zeile, Werkzeug-Launcher und Noten-Edits, die
// freie Taktfläche nur die Takt-Editoren.
//
// **Das native Browser-Menü bleibt in genau zwei Fällen** (kritisch – nicht auf „nur bei Note"
// rückbauen): der Klick lag außerhalb JEDES Takts (`hit === null`), oder der Takt-Bereich hätte
// hier nichts anzubieten – auf der Griffschrift-Ausgabe sind die Takt-Editoren über
// `showAdvancedTools` abgeschaltet, die Karte wäre ein leerer Kopf.
function onContextMenu(e: MouseEvent): void {
  // Während eines Rubber-Band-Drags nur das native Menü unterdrücken – die Geste selbst bricht
  // schon der pointerdown der Sekundärtaste ab (useScoreDragSelection).
  if (isDragSelecting.value) {
    e.preventDefault();
    return;
  }
  const hit = handleContainerContextMenu(e);
  if (!hit || !actionsMenuHasContent(hit.scope, showAdvancedTools)) return;
  e.preventDefault();
  gripAnchor.value = {x: e.clientX, y: e.clientY};
  showGripPopover.value = true;
}

// Doppelklick → Aktionsmenü am Cursor (die Auswahl/den Takt-Kontext und `actionScope` setzt
// `handleContainerClick` synchron, das Lesen direkt danach ist also sicher).
function onContainerClick(e: MouseEvent): void {
  // Nach einem Rubber-Band-Drag (oder dessen Esc-Abbruch) feuert das mouseup noch ein click –
  // genau EINMAL schlucken, sonst überschriebe der Klickpfad die frische Rechteck-Auswahl.
  if (consumeDragClick()) return;
  handleContainerClick(e);
  if (e.detail !== 2 || selectedMeasure.value === null) return;
  if (!actionsMenuHasContent(actionScope.value, showAdvancedTools)) return;
  gripAnchor.value = {x: e.clientX, y: e.clientY};
  showGripPopover.value = true;
}

// Toolbar-Buttons Akkord/Tonart/Taktart/Bass setzen `activeEditTab` – dann öffnet dasselbe Overlay
// direkt im Editor. (Das Overlay setzt den Reiter beim Schließen auf 'note' zurück.)
watch(activeEditTab, (tab) => {
  if (tab !== 'note') showGripPopover.value = true;
});

// Anker des Overlays: im Editor-Modus die gewählte Note (`selectedNoteX/Y` sind relativ zum
// Scroller und werden bei Auswahlwechsel/Scroll neu berechnet → das Overlay wandert beim
// Akkord-Auto-Save mit), ohne Note oben rechts am Scroller; im Menü-Modus der Cursor.
const EDITOR_NOTE_OFFSET = 24;
const actionsAnchor = computed(() => {
  const scroller = scrollContainerRef.value;
  if (activeEditTab.value === 'note' || !scroller) return gripAnchor.value;
  const rect = scroller.getBoundingClientRect();
  if (selectedNoteX.value === null || selectedNoteY.value === null) {
    // Ohne Note: im Takt-Bereich der Cursor (dort ist immer eine Zeigegeste vorausgegangen), im
    // Noten-Bereich oben rechts – dort ist der Fallback für den Toolbar-Weg ohne Auswahl gedacht.
    return actionScope.value === 'measure' ? gripAnchor.value : {x: rect.right, y: rect.top};
  }
  return {
    x: rect.left + selectedNoteX.value,
    y: rect.top + selectedNoteY.value + EDITOR_NOTE_OFFSET,
  };
});

// Rechtsklick-Launcher angeklickt → der (einzige) Griff-Dialog öffnet (und schließt das Menü
// damit strukturell).
function openGripFromLauncher(): void {
  showGripDialog.value = true;
}

// Gewählte Alternative aus dem Griff-Dialog nach oben reichen (Parent tauscht den Griff der
// Ausgabe-Note client-seitig via applyGripToConvertedNote) und Dialog/Launcher schließen.
function onGripChosen(grip: Grip): void {
  // Die betroffene Note MIT emittieren (wie `applyBassLyric`): sonst müsste der Parent per
  // Template-Ref in diese Komponente zurückgreifen, um die Auswahl zu lesen, die hier ohnehin
  // im Scope liegt.
  const note = selectedNote.value;
  if (note) emit('applyGrip', {note, grip});
  closeOverlays();
}

// ============ Tonhöhen-Tooltip beim Hovern (beide Vorschauen) ============

// Offene Overlays verdrängen den Tooltip – sie verschlucken `mousemove`, der Hover-Zustand friert
// sonst auf seinem letzten Wert ein und die Box hinge sichtbar daneben fest.
const hoverTooltipBlocked = computed(() => hoverTooltipSuppressed({
  activeOverlay: activeOverlay.value,
  lyricEntryActive: isLyricActive.value,
  sourceEditorOpen: showSourceEditor.value,
  deleteChoiceOpen: deleteChoicePending.value,
}));

// Genau EINE Unterdrückungs-Stelle: die unterdrückte Note geht weder in die Beschriftung noch in
// den Griff-Abruf der Ausgabe.
const hoveredTooltipNote = computed(() => (hoverTooltipBlocked.value ? null : hoveredNote.value));

// ============ Bass-Notation (nur bei enableBassEdit – Griffschrift-Ausgabe) ============

// Pausen und unsichtbare Bass-Träger tragen die Bass-Lyric ebenso → jedes ausgewählte Element ist Ziel.
const canEditBass = computed(() => !!selectedNote.value);
// Vorbelegung des Popovers (Text der primären Note). Nur bei offenem Popover parsen – kein
// DOM-Parse je Auswahländerung.
const currentBassText = computed(() =>
    showBassPopover.value && selectedNote.value && musicXml.value
        ? readBassLyricText(musicXml.value, selectedNote.value)
        : null,
);
const bassTargetCount = computed(() => selectedNotes.value.length || (selectedNote.value ? 1 : 0));

// Ziel = gesamte (Mehrfach-)Auswahl, sonst die primäre Note.
function bassTargets(): NoteInfo[] {
  if (selectedNotes.value.length) return [...selectedNotes.value];
  return selectedNote.value ? [selectedNote.value] : [];
}

function openBassPopover(): void {
  if (!canEditBass.value) return;
  showBassPopover.value = true;
}

// Rechtsklick-Launcher „Bass bearbeiten" → Popover öffnet (und schließt das Menü strukturell).
function openBassFromLauncher(): void {
  showBassPopover.value = true;
}

// Text/Löschen nach oben reichen (Parent schreibt via applyBassLyricToConvertedNotes) + schließen.
function onBassApply(text: string): void {
  emit('applyBassLyric', {notes: bassTargets(), text});
  showBassPopover.value = false;
}

function onBassRemove(): void {
  emit('applyBassLyric', {notes: bassTargets(), text: null});
  showBassPopover.value = false;
}

// Ohne Auswahl (Esc in der Toolbar, Neukonvertierung → clearSelection) schließt das Popover.
watch(selectedNote, (note) => {
  if (!note) showBassPopover.value = false;
});

// ============ Gesangstext (nur bei enableLyricEdit – beide Vorschauen) ============

// Pausen tragen keine Silbe. Zusätzlich gesperrt, solange ungespeicherte Struktur-Edits anliegen:
// die Übernahme am Sitzungsende ändert den `file`-Prop, dessen Watcher `musicXml` überschreibt und
// `hasUnsavedChanges` zurücksetzt – die schwebenden Edits wären still verloren.
const canEditLyric = computed(() =>
    !!selectedNote.value && !selectedNote.value.isRest && !hasUnsavedChanges.value,
);
const lyricBlockedReason = computed(() => {
  if (hasUnsavedChanges.value) return 'Bitte zuerst Übernehmen oder Verwerfen';
  if (!selectedNote.value || selectedNote.value.isRest) return 'Erst eine klingende Note wählen';
  return '';
});

function openLyricEntry(): void {
  if (!canEditLyric.value) return;
  // Die Sitzung ist kein Overlay im Enum – schwebende Overlays deshalb explizit schließen.
  closeOverlays();
  startLyricEntry();
}

// Rechtsklick-Launcher „Gesangstext" → Sitzung starten (schließt die Overlays selbst).
function openLyricFromLauncher(): void {
  openLyricEntry();
}

// Eine Sitzung = EIN Undo-Schritt: `useRefHistory` snapshottet den ganzen XML-String, 40 Silben
// wären sonst 40 Volldokument-Snapshots und würden den 50er-Puffer komplett verdrängen.
watch(isLyricActive, (active) => {
  if (active) {
    pauseHistory();
    return;
  }
  resumeHistory();
  commitHistory();
});

// Die drei Zusatz-Werkzeuge der (Kind-)ScoreEditToolbar in EINEM Kontext bereitstellen –
// Dialog/Popover/Sitzung leben hier. Immer providen; `enabled` je Werkzeug spiegelt die Props,
// sodass Griff/Bass nur auf der Ausgabe-Vorschau erscheinen.
provideToolbarExtensions({
  grip: {
    enabled: computed(() => enableGrips),
    canEdit: canEditGrip,
    open: () => {
      showGripDialog.value = true;
    },
  },
  bass: {
    enabled: computed(() => enableBassEdit),
    canEdit: canEditBass,
    open: openBassPopover,
  },
  lyric: {
    enabled: computed(() => enableLyricEdit),
    canEdit: canEditLyric,
    blockedReason: lyricBlockedReason,
    open: openLyricEntry,
  },
});

// ============ Export Functions ============

async function exportPdf() {
  try {
    await exportPdfFromOsmd(getOsmd());
  } catch (e) {
    console.error('PDF-Export (Ansicht) fehlgeschlagen:', e);
    notificationStore.notify('PDF-Export fehlgeschlagen.', 'error');
  }
}

const {exportPdf: exportPdfFromOsmd} = usePdfExport();

function showMusicXml() {
  showSourceEditor.value = !showSourceEditor.value;
}

// ============ Undo/Redo/Save ============

function handleUndo() {
  undo();
}

function handleRedo() {
  redo();
}

function saveChanges() {
  if (!musicXml.value) return;
  hasUnsavedChanges.value = false;
  emit('saveXml', {musicXml: musicXml.value});
}

function discardChanges() {
  if (!file) return;
  musicXml.value = file;
  hasUnsavedChanges.value = false;
}

// ============ Watchers & Lifecycle ============

// Externen Inhalt (Prop) in den internen musicXml-State übernehmen. Der Prop liefert stets
// bereits entpacktes MusicXML inline (alle Backend-Endpunkte geben es so zurück).
// Das eigentliche Rendern übernimmt useOsmdInteractiveScore (reaktiv auf musicXml).
watchDebounced(() => file, async (newValue) => {
  // Externer Inhaltswechsel (neue Datei/Transpose): Auswahl verwerfen, damit das Composable
  // nach dem Re-Render keine fremde Note an der alten Klick-Position markiert.
  clearSelection();
  if (!newValue) {
    musicXml.value = null;
    return;
  }

  applyingExternalContent = true;
  musicXml.value = newValue;
  await nextTick();
  applyingExternalContent = false;
  hasUnsavedChanges.value = false;
}, {immediate: true, debounce: 50});

// Edits am Quelltext (Live-Editor) bzw. an Note/Akkord/Tonart (Edit-Aktionen im Composable
// mutieren musicXml) markieren ungespeicherte Änderungen; das externe Laden ist per Guard
// ausgenommen.
watch(musicXml, () => {
  if (applyingExternalContent) return;
  hasUnsavedChanges.value = true;
});
</script>

<template>
  <!-- `overflow-clip` statt `overflow-hidden` (kritisch für die Toolbar): `hidden` macht die Wurzel zum
       Scroll-Container, und `sticky top-0` klebte dann an ihr statt am scrollenden `<main>`. Dasselbe
       gilt für alle Vorfahren bis `<main>` (`PreviewTabsCard`, `PreviewPane`). `@container/preview`
       steuert die Toolbar-Dichte (`ToolbarGroup`) und den Quelltext-Split nach der Vorschau-Breite. -->
  <div class="@container/preview flex h-full w-full flex-col overflow-clip rounded-xl border border-gray-200 bg-white shadow-sm">
  <!-- Toolbar -->
  <div class="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white/95 p-3 backdrop-blur-sm">

  <!-- Aktions-Werkzeugleiste der Auswahl (State via inject): Transponieren/Kopieren/Einfügen/
       Bindebogen/Teilen/Löschen + Akkord/Tonart-Öffner. Ersetzt den früheren SelectionIndicator. -->
  <ScoreEditToolbar class="flex-auto order-last" :show-advanced-tools="showAdvancedTools"/>

  <!-- Undo/Redo/Save Actions -->
  <div class="flex items-center gap-1">
    <button
        v-if="musicXml"
        type="button"
        class="btn btn-ghost text-xs py-1.5 px-2 flex items-center gap-1"
        :class="{ 'opacity-40 cursor-not-allowed': !canUndo }"
        :disabled="!canUndo"
        @click="handleUndo"
        title="Rückgängig (Ctrl+Z)"
    >
      <Icon icon="fa6-solid:rotate-left"/>
      <span class="hidden sm:inline">Rückgängig</span>
    </button>
    <button
        v-if="musicXml"
        type="button"
        class="btn btn-ghost text-xs py-1.5 px-2 flex items-center gap-1"
        :class="{ 'opacity-40 cursor-not-allowed': !canRedo }"
        :disabled="!canRedo"
        @click="handleRedo"
        title="Wiederholen (Ctrl+Y)"
    >
      <Icon icon="fa6-solid:rotate-right"/>
      <span class="hidden sm:inline">Wiederholen</span>
    </button>
    <button
        v-if="hasUnsavedChanges"
        type="button"
        class="btn btn-success text-xs py-1.5 px-3 flex items-center gap-2 animate-pulse"
        @click="saveChanges"
        title="Änderungen übernehmen"
    >
      <Icon icon="fa6-solid:check"/>
      <span class="hidden sm:inline">Übernehmen</span>
    </button>
    <button
        v-if="hasUnsavedChanges"
        type="button"
        class="btn btn-ghost bg-red-400 text-xs py-1.5 px-2 text-red-500 hover:text-red-700"
        @click="discardChanges"
        title="Änderungen verwerfen"
    >
      <Icon icon="fa6-solid:xmark"/>
    </button>
  </div>

  <!-- Other Actions -->
  <div class="flex grow-0 max-w-lg items-center gap-2">
    <!-- Status der automatischen Akkorderkennung (nur Eingabe-Vorschau, siehe Prop-Kommentar) -->
    <ChordInferenceBadge
        v-if="showChordInferenceStatus && musicXml"
        :status="chordInference"
        variant="compact"
    />
    <button
        v-if="musicXml"
        type="button"
        class="btn btn-glass"
        :class="showSourceEditor ? 'btn-primary' : 'btn-secondary'"
        @click="showMusicXml"
        :title="showSourceEditor ? 'Quelltext-Editor schließen' : 'MusicXML-Quelltext live bearbeiten'"
    >
      <Icon icon="fa6-solid:code"/>

    </button>
    <button
        v-if="musicXml"
        type="button"
        class="btn btn-glass"
        @click="exportPdf"
        title="Als PDF exportieren (Ansicht/Browser, ohne Server)"
    >
      <Icon icon="fa6-solid:file-pdf"/>
    </button>

  </div>

  <!-- Audio-Player (osmd-extended): Wiedergabe mit Cursor-Verfolgung + Mixer/Tempo/Metronom
       (State via inject) -->

  <!-- Zoom Controls: während der Wiedergabe gesperrt statt ausgeblendet (ein Zoom rendert neu und
       verlöre den Wiedergabe-Cursor; Ausblenden ließ die Toolbar springen). -->
  <fieldset v-if="musicXml" :disabled="playback.isPlaying.value" class="m-0 min-w-0 border-0 p-0">
    <ZoomControls v-model:zoom="zoom"/>
  </fieldset>
  <div class="flex w-full flex-wrap items-center gap-2">
    <template v-if="showInputTransformPanels">
      <VoiceMergePanel class="flex w-full max-w-lg flex-auto animate-pulse sm:w-sm" variant="compact"></VoiceMergePanel>
      <ChordInferencePanel variant="compact"/>
    </template>
    <button
        type="button"
        class="btn btn-glass ml-auto flex-none"
        @click="showShortcuts = true"
        title="Tastenkürzel anzeigen (?)"
        aria-label="Tastenkürzel anzeigen"
    >
      <Icon icon="fa6-solid:circle-question"/>
    </button>
  </div>
  </div>

  <!-- Body: Vorschau + Edit-Panel-Overlay (relative als Anker für das absolute Overlay) -->
  <div class="relative flex flex-1 flex-col">

    <!-- Content: Split-View – Live-Quelltext-Editor (links) | OSMD-Vorschau (rechts) -->
    <div class="relative flex min-h-[40dvh] min-w-0 grow flex-col @4xl/preview:flex-row">

      <!-- Live-Quelltext-Editor (nur sichtbar, wenn per "Code" eingeblendet) -->
      <div
          v-if="showSourceEditor && musicXml"
          class="flex w-full min-w-0 shrink-0 flex-col border-b border-gray-200 bg-white p-2 @4xl/preview:w-1/2 @4xl/preview:border-r @4xl/preview:border-b-0"
      >
        <div class="relative min-w-0 grow overflow-auto bg-gray-50/50 p-4 min-h-[40dvh] max-h-[60dvh] @4xl/preview:min-h-[50dvh] @4xl/preview:max-h-[90dvh]">
          <MusicXmlSourceEditor v-model:value="musicXml" class="min-h-0 flex-1"/>
        </div>

      </div>

      <!-- OSMD-Vorschau (rendert live beim Tippen) -->
      <div ref="scrollContainerRef" class="relative min-w-0 grow overflow-auto bg-gray-50/50 p-4 min-h-[50dvh]">
        <!-- OSMD-Render-Target: volle Breite. OSMD liest container.offsetWidth als Renderbreite. -->
        <!-- @dragstart.prevent: Die Partitur ist NIE eine native Drag-Quelle. Ohne das startet
             ein Klick-Halten-Ziehen einen HTML5-Drag von OSMDs Cursor-<img> (Browser melden ein
             gezogenes Bild als dataTransfer.types ['Files'] → das window-weite Upload-Overlay
             erschiene, siehe shared/windowFileDrag.ts). Zugleich die Grundlage der
             Rubber-Band-Mehrfachauswahl: Klick-Halten-Ziehen bleibt ein reines
             Pointer-Gestenfeld (@pointer* → useScoreDragSelection). -->
        <div
            id="containerRef"
            ref="containerRef"
            class="w-full cursor-pointer transition-transform duration-200 ease-out"
            @click="onContainerClick"
            @mousemove="handleContainerHover"
            @mouseleave="handleContainerMouseLeave"
            @contextmenu="onContextMenu"
            @dragstart.prevent
            @pointerdown="handleContainerPointerDown"
            @pointermove="handleContainerPointerMove"
            @pointerup="handleContainerPointerUp"
            @pointercancel="cancelDragSelection"
        ></div>

        <!-- Rubber-Band-Auswahlrechteck: direktes absolutes Kind des Scrollers in
             CONTENT-Koordinaten (NICHT im inset-0-Overlay unten – das deckt nur die sichtbare
             Scroller-Box): so klebt die Startecke beim Mausrad-Scrollen am Notenbild, und
             overflow-auto clippt an der Scroller-Kante. z-10 bewusst UNTER den Overlays (z-20). -->
        <div
            v-if="dragRect"
            class="pointer-events-none absolute z-10 rounded-sm border border-sky-500/70 bg-sky-400/10"
            :style="{
              left: `${dragRect.left}px`,
              top: `${dragRect.top}px`,
              width: `${dragRect.width}px`,
              height: `${dragRect.height}px`,
            }"
        ></div>

        <!-- Absolute Overlays ÜBER der Partitur (scrollContainerRef ist der relative Anker).
             pointer-events-none lässt Klicks zur Partitur durchfallen; nur die Boxen selbst sind
             interaktiv. -->
        <div class="pointer-events-none absolute inset-0 z-20">
          <PlaybackControlsOverlay/>

          <LyricEntryOverlay v-if="enableLyricEdit"/>
          <!-- Bass-Notation der Ausgabe: Popover unterhalb der primären Note (nur enableBassEdit). -->
          <BassLyricPopover
              v-if="enableBassEdit"
              v-model:isOpen="showBassPopover"
              :current-text="currentBassText"
              :note-count="bassTargetCount"
              @apply="onBassApply"
              @remove="onBassRemove"
          />
        </div>

        <!-- Loading/Empty State -->
        <div v-if="!musicXml" class="absolute inset-0 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
          <Icon icon="tabler:clef-staff" class="text-4xl mb-3 opacity-20"/>
          <p>Noch keine Datei geladen.</p>
        </div>

        <!-- Error State -->
        <div v-if="renderError" class="mt-4 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-800">
          <div class="flex items-center gap-2 font-semibold mb-2">
            <Icon icon="fa6-solid:circle-exclamation"/>
            <span>Fehler bei der Darstellung</span>
          </div>
          <p class="mb-2">Die MusicXML-Vorschau konnte nicht geladen werden.</p>
          <button
              type="button"
              class="text-xs text-red-600 hover:text-red-800 underline flex items-center gap-1"
              @click="showErrorDetails = !showErrorDetails"
          >
            {{ showErrorDetails ? 'Details ausblenden' : 'Details anzeigen' }}
            <Icon :icon="showErrorDetails ? 'fa6-solid:chevron-up' : 'fa6-solid:chevron-down'"/>
          </button>
          <pre v-if="showErrorDetails" class="mt-2 p-3 bg-white/50 rounded-lg border border-red-100 text-xs font-mono whitespace-pre-wrap break-words">{{ renderError }}</pre>
        </div>
      </div>
    </div>
  </div>

  <!-- Tastenkürzel-Übersicht (Overlay), geöffnet über „?"-Button oder Taste „?" -->
  <KeyboardShortcutsDialog v-model:isOpen="showShortcuts"/>
  <DeleteChoiceDialog/>

  <!-- Rechtsklick-Kontextmenü der Note (BEIDE Vorschauen) + der Griff-Inspector-Dialog (nur Ausgabe,
       geöffnet über den Toolbar-Button ODER das Menü – NoteActionsOverlay emittiert @open). Der
       Launcher-Eintrag „Bass bearbeiten" (@open-bass → BassLyricPopover) und „Gesangstext"
       (@open-lyric) hängen an ihren Flags; Takt-Editoren (rendert das Overlay selbst, gegatet
       über showAdvancedTools) und Noten-Edits holt es sich aus dem ScoreEditContext. Geöffnet per
       Rechtsklick, Doppelklick (onContainerClick) und über die Takt-Buttons der Toolbar. Betrifft die aktuelle Auswahl; der Parent schreibt per @apply-grip /
       @apply-bass-lyric in conversionResult. -->
  <NoteGripDialog
      v-if="enableGrips"
      v-model:isOpen="showGripDialog"
      :music-xml="musicXml"
      :selected-note="selectedNote"
      @apply-grip="onGripChosen"
  />
  <NoteActionsOverlay
      v-model:isOpen="showGripPopover"
      :anchor-x="actionsAnchor.x"
      :anchor-y="actionsAnchor.y"
      :show-grip="enableGrips"
      :show-bass="enableBassEdit"
      :show-lyric="enableLyricEdit"
      :show-measure-edits="showAdvancedTools"
      :scope="actionScope"
      :music-xml="musicXml"
      :selected-note="selectedNote"
      @open="openGripFromLauncher"
      @open-bass="openBassFromLauncher"
      @open-lyric="openLyricFromLauncher"
  />
  <!-- Tonhöhe der Note unter dem Zeiger. `fixed` + `pointer-events-none` – die Stelle im Template
       ist deshalb beliebig, und der Tooltip fängt weder Hover noch Klicks ab. -->
  <NoteHoverTooltip
      :note="hoveredTooltipNote"
      :x="hoverX"
      :y="hoverY"
      :prefer-sounding="enableGrips"
      :music-xml="musicXml"
  />
  </div>
</template>
